import { composeState } from '../../state';
import { nextLocalId } from './context';
import { buildToolContentElement } from './tool-content';
import {
  toolGroupSummaryText,
  toolKind,
  toolStateClass,
  toolStateLabel,
  toolSubtitle,
  toolTitle,
} from './tool-model';
import type { ToolCallSegment } from './types';

function applyToolSummary(summary: Element, tool: ToolCallSegment, done: boolean): void {
  summary.innerHTML = '';
  const stateClass = toolStateClass(tool, done);
  const icon = document.createElement('span');
  icon.className = `tool-call-icon ${stateClass}`;

  const main = document.createElement('span');
  main.className = 'tool-call-summary-main';
  const label = document.createElement('span');
  label.className = 'tool-call-label';
  label.textContent = toolTitle(tool);
  main.appendChild(label);

  const subtitleText = toolSubtitle(tool);
  if (subtitleText) {
    const subtitle = document.createElement('span');
    subtitle.className = 'tool-call-subtitle';
    subtitle.textContent = subtitleText;
    main.appendChild(subtitle);
  }

  const state = document.createElement('span');
  state.className = `tool-call-state ${stateClass}`;
  state.textContent = toolStateLabel(tool, done);

  summary.appendChild(icon);
  summary.appendChild(main);
  summary.appendChild(state);
}

export function updateToolCallElement(toolUseId: string, result: unknown): void {
  const el = document.getElementById(`tool-${toolUseId}`) as HTMLElement | null;
  if (!el) return;
  const tool = composeState.activeToolCalls.get(toolUseId) || {
    id: toolUseId,
    name: el.dataset.toolName || '',
    kind: el.dataset.toolKind || null,
    done: true,
  };
  tool.done = true;
  if (result !== undefined) tool.result = result;
  const summary = el.querySelector('summary');
  if (summary) applyToolSummary(summary, tool, true);
  if (tool.name !== 'AskUserQuestion') {
    const nextContent = buildToolContentElement(tool);
    const content = el.querySelector('.tool-call-content');
    if (content) content.replaceWith(nextContent);
  }
  refreshToolGroupSummary(el.closest('.tool-group'));
}

export function createToolCallElement(toolUseId: string, tool: ToolCallSegment, done: boolean): HTMLDetailsElement {
  const details = document.createElement('details');
  details.className = 'tool-call msg-segment';
  details.id = `tool-${toolUseId}`;
  details.dataset.toolName = tool.name || '';
  const kind = toolKind(tool);
  if (kind) {
    details.dataset.toolKind = kind;
    details.classList.add(`codex-${kind.replace(/_/g, '-')}`);
  }
  const summary = document.createElement('summary');
  applyToolSummary(summary, tool, done);
  details.appendChild(summary);
  details.appendChild(buildToolContentElement({ ...tool, done }));
  return details;
}

export function refreshToolGroupSummary(group: Element | null): void {
  if (!group) return;
  const summary = group.querySelector('.tool-group-summary');
  const inner = group.querySelector('.tool-group-inner');
  if (!summary || !inner) return;
  const items = Array.from(inner.querySelectorAll(':scope > .tool-call')).map((call: Element) => {
    const callEl = call as HTMLElement;
    const toolUseId = callEl.id ? callEl.id.replace(/^tool-/, '') : '';
    const active = toolUseId ? composeState.activeToolCalls.get(toolUseId) : null;
    const fallbackTool: ToolCallSegment = {
      id: toolUseId,
      name: callEl.dataset.toolName || '',
      kind: callEl.dataset.toolKind || null,
      done: !callEl.querySelector('.tool-call-state.running'),
      meta: active?.meta || null,
    };
    return active ? { ...active, id: toolUseId } : fallbackTool;
  });
  summary.textContent = toolGroupSummaryText(items);
}

export function createToolGroupElement(toolItems: ToolCallSegment[] = []): HTMLDetailsElement {
  const group = document.createElement('details');
  group.className = 'tool-group msg-segment';
  const summary = document.createElement('summary');
  summary.className = 'tool-group-summary';
  group.appendChild(summary);
  const inner = document.createElement('div');
  inner.className = 'tool-group-inner';
  group.appendChild(inner);
  toolItems.forEach((tool) => {
    const details = createToolCallElement(tool.id || nextLocalId('saved'), tool, tool.done !== false);
    inner.appendChild(details);
  });
  refreshToolGroupSummary(group);
  return group;
}
